import Cookies from 'js-cookie'
import Drift from 'drift-zoom'
import { hideCartModal, fetchCartData, showCartHoverPreview } from "../utils/utils"

// Shopify.formatMoney was previously provided by api.jquery.js — polyfill if absent
if (window.Shopify && !Shopify.formatMoney) {
  Shopify.formatMoney = function(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '')
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/
    const formatString = format || Shopify.money_format || '$ {{amount}}'
    function formatWithDelimiters(number, precision, thousands, decimal) {
      precision = precision == null ? 2 : precision
      thousands = thousands || ','
      decimal = decimal || '.'
      if (isNaN(number) || number == null) return 0
      number = (number / 100).toFixed(precision)
      const parts = number.split('.')
      const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands)
      const pennies = parts[1] ? decimal + parts[1] : ''
      return dollars + pennies
    }
    const match = formatString.match(placeholderRegex)
    let value = ''
    switch (match && match[1]) {
      case 'amount':                             value = formatWithDelimiters(cents, 2); break
      case 'amount_no_decimals':                 value = formatWithDelimiters(cents, 0); break
      case 'amount_with_comma_separator':        value = formatWithDelimiters(cents, 2, '.', ','); break
      case 'amount_no_decimals_with_comma_separator': value = formatWithDelimiters(cents, 0, '.', ','); break
      default:                                   value = formatWithDelimiters(cents, 2)
    }
    return formatString.replace(placeholderRegex, value)
  }
}

// -----------------------------------------------
// SET REUSABLE VARIABLES
// -----------------------------------------------

const MONEY_FORMAT = '$ {{amount}}'

// -----------------------------------------------
// SET UP QUEUE FOR AJAX CALLS
// -----------------------------------------------

const Queue = () => {
	let previous = Promise.resolve()
	return (fn, fail) => (previous = previous.then(fn, fail || fn))
}
const queue = Queue()

/**
 * Updates the cart totals in the header and cart page
 *
 * @param {function} callback - A callback function to run after the cart totals have been updated
 */

const updateCartTotals = (callback) => {
	queue(() =>
		fetch('/cart.js', { cache: 'no-store' })
			.then(res => res.json())
			.then(cart => {
				const count = cart.item_count
				document.querySelectorAll('.nav-cart-count').forEach(el => { el.textContent = count < 99 ? count : '99+' })
				document.querySelectorAll('.cart-items-pluralize').forEach(el => { el.textContent = count < 2 ? 'Item' : 'Items' })
				document.querySelectorAll('.cart-items-count').forEach(el => { el.textContent = count })
				document.querySelectorAll('.items-subtotal-price').forEach(el => { el.innerHTML = Shopify.formatMoney(cart.total_price, MONEY_FORMAT) })
				document.querySelectorAll('.cart-total').forEach(el => { el.innerHTML = Shopify.formatMoney(cart.total_price, MONEY_FORMAT) })
				if (callback) callback(cart)
			})
			.catch(err => console.error('Failed to update cart totals: ', err))
	)
}

/**
 *
 * Updates the quantity of a specific line item in the shopping cart
 *
 * @param {number} lineItemKey - The key of the line item to update
 * @param {number} lineItemQuantity - The new quantity of the line item
 */

const updateCartLineItem = (lineItemKey, lineItemQuantity) => {
	queue(() =>
		fetch('/cart/change.js', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ id: lineItemKey, quantity: lineItemQuantity }),
		})
			.then(res => res.json())
			.then(cart => {
				const lineItems = document.querySelectorAll('.cart-item-item')

				// $.each was (index, value); forEach is (value, index) — arguments correctly swapped
				cart.items.forEach((lineItem, i) => {
					const lineItemEl = lineItems[i]
					if (!lineItemEl) return
					const lineItemSinglePrice = lineItemEl.querySelector('.item-single-price')
					const lineItemPrice = lineItemEl.querySelector('.item-line-price')

					if (lineItemPrice) lineItemPrice.textContent = Shopify.formatMoney(lineItem.final_line_price, MONEY_FORMAT)
					if (lineItemSinglePrice) {
						lineItem.quantity <= 1
							? lineItemSinglePrice.classList.add('hide')
							: lineItemSinglePrice.classList.remove('hide')
					}

					const itemMessage = lineItemEl.querySelector('.item-message')
					if (lineItem.discounts.length || lineItem.line_level_discount_allocations.length) {
						const discountMessage = lineItem.discounts.map(d => d.title).join(', ')
						const discountSpan = document.createElement('span')
						discountSpan.textContent = ' ' + discountMessage + ' '
						if (itemMessage) {
							itemMessage.innerHTML = ''
							itemMessage.appendChild(discountSpan)
							itemMessage.style.display = ''
						}
						if (lineItemPrice) lineItemPrice.classList.add('discount')
					} else if (itemMessage) {
						itemMessage.innerHTML = ''
						itemMessage.style.display = 'none'
					}
				})

				updateCartTotals()
			})
			.catch(err => {
				console.error('Failed to update: ', err)
				alert('The quantity that you entered is not available. Please select a lower quantity to continue')
			})
	)
}


/**
 * Updates the quantity of a specific line item in the shopping cart
 *
 * @param {number} variantID - The ID of the variant to update
 * @param {number} quantity - The new quantity of the line item
 *
 */
const updateCartLineItemVariant = (lineItemKey, newVariantId, quantity, removeIndex) => {

	fetchCartData()
	  .then(cart => {
		// find the index of the item
		const index = cart.items.findIndex(i => i.key === lineItemKey);
		// Handle the case where the index is not found
		if (index === -1) {
			console.error(`Item not found in the cart.`);
			alert('The item you are trying to update was not found in the cart.')
			return;
		}
		const item = cart.items[index];

		// get the value of the variant to check if engraving is selected
		const domItems = document.querySelectorAll(`[data-key="${lineItemKey}"]`);
		 // Process engraving properties
		const { engravingSelected, engravingLine1, engravingLine2, dogName} = processEngravingProperties(
			domItems,
			item.properties
		);


		// Merge engraving properties with existing properties
		const mergedProperties = mergeProperties(item.properties, engravingSelected, engravingLine1, engravingLine2, dogName);

		// Remove old line item
		return removeCartLineItem(removeIndex).then(() => mergedProperties);
	  })
	  .then(mergedProperties => {
		return fetch('/cart/add.js', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ id: newVariantId, quantity, properties: mergedProperties }),
		}).then(res => res.json())
	  }).then(() => {
		// Successful AJAX request, update the cart totals
	  	updateCartTotals()
		// refresh page
		window.location.reload()
	  }).catch(error => {
		// Failed AJAX request, log the error, message the user, and re-enable the inputs
		console.error("Failed to update: ", error)
		alert('The option you selected is not available, please pick a different option.')
	  })
}

const processEngravingProperties = (domItems, itemProperties) => {
	let engravingLine1 = '';
	let engravingLine2 = '';
	let dogName = '';
	let engravingSelected = false;

	// Iterate over domItems to process each element
	domItems.forEach(domItem => {
		if (domItem.tagName === 'SELECT') {
		// Handle <select> elements
			if (domItem.value.toLowerCase().includes('engraving') || domItem.value.toLowerCase().includes('yes')) {
				engravingSelected = true;
			}
		} else if (domItem.tagName === 'INPUT') {
				// Handle <input> elements
				if (domItem.name === 'Engraving Line 1') {
					engravingLine1 = domItem.value.trim();
				} else if (domItem.name === 'Engraving Line 2') {
					engravingLine2 = domItem.value.trim();
				} else if (domItem.name === 'Dog Name') {
					dogName = domItem.value.trim();
				}
		}
	})

	// Fallback to existing properties if inputs are not found
	engravingLine1 = engravingLine1 || itemProperties?.['Engraving Line 1'] || '';
	engravingLine2 = engravingLine2 || itemProperties?.['Engraving Line 2'] || '';
	dogName = dogName || itemProperties?.['Dog Name'] || '';

	return {
	  engravingSelected,
	  engravingLine1,
	  engravingLine2,
	  dogName
	};
};

const mergeProperties = (itemProperties, engravingSelected, engravingLine1, engravingLine2, dogName) => {
	return {
	  // Include all existing properties
	  ...itemProperties,
	  ...(engravingSelected
		? {
			// Add engraving properties if engraving is selected
			'Engraving Line 1': engravingLine1,
			'Engraving Line 2': engravingLine2,
			'Dog Name': dogName,
		  }
		: {
			// Remove engraving properties if engraving is not selected
			'Engraving Line 1': undefined,
			'Engraving Line 2': undefined,
			'Dog Name': undefined,
		  }),
	};
};
/**
 * Removes a specific line item from the shopping cart
 *
 * @param {number} index - The index of the line item to remove
 */

const removeCartLineItem = (index) => {
	return fetch('/cart/change.js', {
		method: 'POST',
		headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify({ quantity: 0, line: index }),
	})
		.then(res => {
			if (!res.ok) throw new Error('Failed to remove item')
			return res.json()
		})
}

/**
 * Adds a product to the shopping cart.
 *
 * @param {jQuery} $btn - The add-to-cart button
 * @param {function} callback - A callback function to execute after the product is added
 */

const addToCart = (btn, callback) => {
    btn.style.display = 'none'
    const qtyArea = document.getElementById('QtyArea')
    qtyArea?.classList.toggle('QtyAreaVisibilityToggle')
    const spinner = document.createElement('div')
    spinner.className = 'spinner-add-to-cart sk-spinner sk-spinner-pulse'
    btn.insertAdjacentElement('afterend', spinner)

    const formData = new FormData(document.getElementById('AddToCartForm'))

    queue(() =>
        fetch('/cart/add.js', { method: 'POST', body: formData })
            .then(res => res.json())
            .then(() => {
                document.querySelectorAll('.checkout-callout, .added-success-message').forEach(el => el.remove())

                const priceQtyAdd = document.getElementById('PriceQtyAdd')
                if (priceQtyAdd && !document.querySelector('.checkout-callout')) {
                    const checkoutCallout = document.createElement('div')
                    checkoutCallout.className = 'checkout-callout'
                    checkoutCallout.innerHTML = "<p>Cart <span class='cart-items-count'></span> Items | <span class='cart-total'></span></p><a class='btn' href='/cart'>Checkout</a>"
                    checkoutCallout.style.display = 'none'
                    priceQtyAdd.insertAdjacentElement('afterend', checkoutCallout)
                    updateCartTotals(() => { checkoutCallout.style.display = '' })
                } else {
                    updateCartTotals()
                }

                initCartSelectWidth()
                if (callback) callback()
                document.querySelectorAll('.spinner-add-to-cart').forEach(el => el.remove())

                setTimeout(() => {
                    btn.style.display = ''
                    qtyArea?.classList.toggle('QtyAreaVisibilityToggle')
                }, 3000)
            })
            .catch(() => {
                alert('The quantity that you entered is not available. Please select a lower quantity to continue')
                document.querySelectorAll('.spinner-add-to-cart').forEach(el => el.remove())
                btn.style.display = ''
                qtyArea?.classList.toggle('QtyAreaVisibilityToggle')
            })
    )
}

/**
 * Adds a product to the shopping cart.
 *
 * @param {jQuery Object} $btn - The button object that triggers this function.
 * @param {number} product_id - The ID of the product to add to the cart.
 * @param {number} quantity - The quantity of the product to add.
 * @param {Object} properties - Additional properties related to the product.
 *
 */

document.addEventListener('DOMContentLoaded', () => {

	// -----------------------------------------------
	// BREED CHOOSER — native select, Chosen.js removed
	// -----------------------------------------------

	document.querySelectorAll('.breed-chooser').forEach(el => {
		Object.assign(el.style, { display: 'block', padding: '10px 0 0 0', borderBottom: 'none' })
	})
	document.querySelectorAll('.breed-chooser select').forEach(el => { el.style.display = '' })
	document.querySelectorAll('.breed-chooser').forEach(el => {
		const inner = el.closest('.inner')
		if (inner) inner.style.paddingLeft = '0px'
	})
	document.querySelectorAll('.breed-chooser select').forEach(select => {
		select.addEventListener('change', function() { window.location.href = this.value })
	})

	// -----------------------------------------------
	// HEADER BREED SELECTOR RANDOMIZER
	// -----------------------------------------------

	const breedSelectorImages = document.getElementById('header-breed-selector-images')
	if (breedSelectorImages) {
		const imgs = breedSelectorImages.querySelectorAll('img')
		const img = imgs[Math.floor(Math.random() * imgs.length)]
		if (img) {
			const breedName = img.getAttribute('data-breed-name')
			const selectEl = document.querySelector('#header-breed-selector .breed-chooser select')
			if (selectEl) {
				const option = selectEl.querySelector(`option[data-breed-name="${breedName}"]`)
				if (option) option.selected = true
				setTimeout(() => {
					const link = img.closest('a')
					if (link) link.href = selectEl.value
					img.style.display = ''
					img.style.opacity = '1'
				}, 500)
			}
		}
	}

	// -----------------------------------------------
	// PRODUCT IMAGES
	// -----------------------------------------------

	let _drift = null

	document.querySelectorAll('.product-single__thumbnail').forEach(thumb => {
		thumb.addEventListener('click', function(e) {
			e.preventDefault()
			const productImageMain = document.getElementById('product-image-main')
			if (_drift) { _drift.destroy(); _drift = null }
			const imgSrc = this.querySelector('.product-img-large')?.src
			const imgSrcZoom = this.getAttribute('data-zoom')
			if (imgSrc) {
				const photoImg = document.getElementById('ProductPhotoImg')
				if (photoImg) photoImg.src = imgSrc
				if (productImageMain) productImageMain.setAttribute('data-zoom', imgSrcZoom)
			}
		})
	})

	;['product-image-zoom-toggle', 'product-image-main'].forEach(id => {
		document.getElementById(id)?.addEventListener('click', function(e) {
			e.preventDefault()
			if (window.matchMedia('(max-width: 1024px)').matches) {
				if (e.currentTarget.id === 'product-image-main') return
			}
			const productImageMain = document.getElementById('product-image-main')
			if (this.classList.contains('zoom-enabled')) {
				if (_drift) { _drift.destroy(); _drift = null }
				this.classList.remove('zoom-enabled')
			} else {
				this.classList.add('zoom-enabled')
				const paneContainer = document.getElementById('product-image-zoom-pane') || (() => {
					const pane = document.createElement('div')
					pane.id = 'product-image-zoom-pane'
					productImageMain?.insertAdjacentElement('afterend', pane)
					return pane
				})()
				const trigger = productImageMain?.querySelector('img') || productImageMain
				if (trigger) {
					_drift = new Drift(trigger, {
						paneContainer,
						zoomFactor: 2,
						inlinePane: false,
					})
				}
			}
		})
	})
	// -----------------------------------------------

	document.getElementById('btn-forgot-password')?.addEventListener('click', e => {
		e.preventDefault()
		document.getElementById('form-container-login').style.display = 'none'
		document.getElementById('form-container-reset-password').style.display = ''
	})

	document.getElementById('btn-cancel-reset-password')?.addEventListener('click', e => {
		e.preventDefault()
		document.getElementById('form-container-reset-password').style.display = 'none'
		document.getElementById('form-container-login').style.display = ''
	})

	if (window.location.hash === '#recover') {
		const loginEl = document.getElementById('form-container-login')
		const resetEl = document.getElementById('form-container-reset-password')
		if (loginEl) loginEl.style.display = 'none'
		if (resetEl) resetEl.style.display = ''
	}

	// -----------------------------------------------
	// ALERT MAX QUANTITY ON CART PAGE
	// -----------------------------------------------

	document.querySelectorAll('[max]').forEach(el => {
		el.addEventListener('change', function() {
			const max = parseInt(this.getAttribute('max'), 10) || 10000
			const value = parseInt(this.value, 10) || 0
			if (value > max) {
				alert('We only have ' + max + ' of this item in stock')
				this.value = max
			}
		})
	})

	// -----------------------------------------------
	// VIEW TOGGLE
	// -----------------------------------------------

	document.querySelectorAll('#view-toggle a').forEach(link => {
		link.addEventListener('click', e => {
			e.preventDefault()
			document.querySelectorAll('#view-toggle a').forEach(a => a.classList.remove('active'))
			link.classList.add('active')
			const view = link.getAttribute('data-view')
			if (view === 'grid') {
				document.querySelector('.collection-grid')?.classList.remove('section-product-boxes-list')
				Cookies.set('collection-view', 'grid')
			} else if (view === 'list') {
				document.querySelector('.collection-grid')?.classList.add('section-product-boxes-list')
				Cookies.set('collection-view', 'list')
			}
		})
	})

	// --------------------------------
	// CART PAGE - INCREMENT QUANTITY
	// --------------------------------

	document.querySelectorAll('.cart-table input.quantity').forEach(input => {
		input.addEventListener('change', function() {
			const lineItemKey = this.dataset.key
			const lineItemQuantity = this.value
			if (lineItemQuantity < 1) {
				alert('Please enter an larger quantity or remove the item from cart')
				return
			}
			updateCartLineItem(lineItemKey, lineItemQuantity)
		})
	})

	// --------------------------------
	// Updates variant in cart
	// --------------------------------

	// Event delegation covers all .properties divs across all cart items
	document.querySelector('.cart-table')?.addEventListener('change', e => {
		if (!e.target.closest('.properties')) return
		const target = e.target
		const lineItemOriginalKey = target.getAttribute('data-key')
		const lineItemKey = lineItemOriginalKey.replace(':', '-')
		const currentItem = document.getElementById('cart-item-' + lineItemKey)
		if (!currentItem) return
		const options = [...currentItem.querySelectorAll('.cart-select-option option:selected')]
		const variants = JSON.parse(currentItem.dataset.variants || '{}')
		const removeIndex = currentItem.dataset.index
		const lineItemQuantity = currentItem.querySelector('.input-quantity')?.value
		const properties = currentItem.querySelectorAll('.property')
		let variant = ''

		if (options.length > 0) {
			options.forEach((opt, i) => {
				variant += opt.value.replace(/"/g, 'in')
				if (i < options.length - 1) variant += '/'
			})
		} else {
			for (const key in variants) {
				if (Object.prototype.hasOwnProperty.call(variants, key)) variant += key
			}
		}

		const propsObj = {}
		properties.forEach(prop => { propsObj[prop.name] = prop.value })
		if (target.tagName === 'INPUT') propsObj[target.name] = target.value

		let variantID = variants[variant]?.id ?? null
		if (variantID === undefined) {
			alert('Variant "' + variant + '" is unavailable at this time')
			options.forEach(opt => { opt.selected = opt.defaultSelected })
			return
		}

		initCartSelectWidth(target)
		if (lineItemOriginalKey && variantID && lineItemQuantity && removeIndex) {
			updateCartLineItemVariant(lineItemOriginalKey, variantID, lineItemQuantity, removeIndex)
		}
	})

	// --------------------------------
	// CONDITIONALLY sets requred arrtibure on fields
	// --------------------------------

	let lastVariantId = null;

	function updateEngravingFields() {
		const variantId = document.querySelector('input[name="id"]')?.value;
		if (!variantId || variantId === lastVariantId) return;

		lastVariantId = variantId;
		// let embroideryOptionSelected;
		const embroiderySelectElement = document.querySelectorAll('.product-form__select');
		const embroideryOptionSelected = embroiderySelectElement?.length > 1 ? embroiderySelectElement[1] : embroiderySelectElement[0];
		const embroideryValue = embroideryOptionSelected?.value.toLowerCase();
		const engravingInputs = document.querySelectorAll(".product-form__input.product-form__input--has-instructions");
		const productAllowsEngraving = window?.productEngravingEnabled === true;

		engravingInputs.forEach((input) => {

		  if (productAllowsEngraving && (embroideryValue?.includes('engraving') || embroideryValue?.includes('yes'))) {
			input.disabled = false;
			input.required = true;
		  } else if(productAllowsEngraving && (!embroideryValue?.includes('engraving') || embroideryValue?.includes('no'))) {
			input.disabled = true;
			input.required = false;
		  } else {
			input.disabled = false;
			input.required = false;
		  }
			// Reset validation state
			input.setCustomValidity('');
			input.classList.remove('product-form__input--invalid');
			input.value = '';
		});
	}

	const variantPicker = document.querySelectorAll('.product__main__variant-picker')[0];

	if(variantPicker) {
		variantPicker.querySelectorAll("select").forEach(select => {
			select.addEventListener("change", updateEngravingFields);
		});
		// initial run
		updateEngravingFields();
	}

	// MutationObserver: detect Shopify DOM re-rendering
	const observerTarget = document.querySelector('form.product-form') || document.body;
	const observer = new MutationObserver(updateEngravingFields);
	observer.observe(observerTarget, {
	  childList: true,
	  subtree: true,
	});

	// --------------------------------
	// CART PAGE - Change Items Options Value
	// --------------------------------

	const initCartSelectWidth = (select) => {
		if (!select.options) return // guard: only applies to <select> elements
		const temp = document.createElement('span')
		temp.style.cssText = 'position:absolute;visibility:hidden;white-space:nowrap'
		temp.textContent = select.options[select.selectedIndex]?.text || ''
		document.body.appendChild(temp)
		let width = temp.offsetWidth
		temp.remove()
		const calculateBuffer = (w) => {
			if (w === 0) return 200
			if (w < 26) return 10
			if (w > 150) return 80
			if (w < 50) return 20
			return 30 + 0.5 * (w - 50)
		}
		width += calculateBuffer(width)
		select.style.width = width + 'px'
	}

	document.querySelectorAll('.cart-table select.cart-select').forEach(select => {
		initCartSelectWidth(select)
		select.addEventListener('change', e => initCartSelectWidth(e.target))
	})


	// -----------------------------------------------
	// DISCOUNTED PRODUCTS REQUIRE PAGE RELOAD ON QUANTITY UPDATE
	// -----------------------------------------------

  	document.querySelectorAll('.cart-table input.quantity-discount').forEach(input => {
		input.addEventListener('change', function() {
			const cell = this.closest('td')
			if (cell && !cell.querySelector('.btn-update-quantity')) {
				const btn = document.createElement('button')
				btn.type = 'submit'
				btn.className = 'btn btn-primary btn-update-quantity'
				btn.style.cssText = 'padding: 5px 10px;font-size:.8em;letter-spacing:.05em;'
				btn.textContent = 'Update'
				cell.appendChild(btn)
			}
		})
	})

	// --------------------------------
	// CART HOVER MODAL FUNCTIONALITY
	// --------------------------------

	// Elements
	const cartIcon = document.querySelector('.logo-bar__links #cart-icon');
	const cartWrapper = document.querySelector('.cart-message-wrapper');
	const cartExitButton = document.querySelector('.cart-message__close');

	if (cartIcon) {
		// Holds the ID returned by setTimeout to be cleared later once the user stops hovering
		let hoverCheckTimeout = null;

		// Hide cart modal when no longer hovering over icon or modal after checking hover state
		function checkHoverState() {
			clearTimeout(hoverCheckTimeout);
			hoverCheckTimeout = setTimeout(() => {
				const stillHovering = cartIcon.matches(':hover') || cartWrapper?.matches(':hover');
				if (!stillHovering) {
					hideCartModal();
				}
			}, 250);
		}

		// Show modal on hover
		cartIcon.addEventListener('mouseenter', showCartHoverPreview);

		// Check when user leaves hover over cart icon
		cartIcon.addEventListener('mouseleave', checkHoverState);

		// Check when user leaves hover over cart message
		cartWrapper?.addEventListener('mouseleave', checkHoverState);

		// Allow manual close with "X" button
		cartExitButton?.addEventListener('click', hideCartModal);
	}

	if (Cookies.get('collection-view') === 'list') {
		document.querySelector('#view-toggle a[data-view="list"]')?.click()
	}

	// -----------------------------------------------
	// RECENTLY VIEWED
	// -----------------------------------------------

	var recently_viewed_max = 4;
var recently_viewed = JSON.parse(Cookies.get('recently-viewed') || '[]');

	addToRecentlyViewed()

	function addToRecentlyViewed() {
		let product_page_url = window.location.href.match(/.*\/products\/.*/g)

		if (product_page_url && product_page_url[0]) {
			let the_url = product_page_url[0].split(/[?#]/)[0]

			if (recently_viewed) {
				var urls = recently_viewed
			} else {
				var urls = Array()
			}

			if (!urls.includes(the_url)) {
				if (urls.length >= recently_viewed_max) {
					urls.shift()
				}
				urls.push(the_url)
			}
			Cookies.set('recently-viewed', urls)
		}
	}
})

